import { fetchExperienceCategories, fetchExperiencesById } from 'actions/ExperienceActions'
import { AnythingCategory, EXP_CATEGORY_DAY_TRIPS_ID, EXP_CATEGORY_FOOD_AND_DRINK_ID, EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID, EXP_CATEGORY_TOP_ACTIVITIES_ID, EXP_CATEGORY_WELLNESS_AND_SPA_ID } from 'constants/experience'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useExperienceSearchList, { type ExperienceSearchListState } from './useExperienceSearchList'
import { usePlaceByCoords } from 'hooks/usePlace'
import { useCallback, useEffect, useMemo, useState } from 'react'

const RECOMMENDATION_MIN_OFFERS = 6
const RECOMMENDATION_MAX_OFFERS = 7
const RECOMMENDATION_CANDIDATE_BATCH = 43

type RecommendationSourceId = 'specific' | 'parent' | 'all' | 'dining' | 'wellness' | 'activities'
export type RecommendationId = 'yaml' | 'similar' | 'new' | 'dining' | 'wellness' | 'activities'

interface RecommendationDefinition {
  id: RecommendationId
  source: RecommendationSourceId
  fallback?: RecommendationSourceId
  enabled: boolean
  categoryIds?: Array<number>
}

interface RecommendationSource {
  list: ExperienceSearchListState
  limit: number
  enabled: boolean
}

export interface RecommendationSection {
  id: RecommendationId
  status: 'loading' | 'hidden' | 'ready'
  experiences: Array<App.ExperienceOffer>
  categoryIds?: Array<number>
  listKey: string
}

interface SelectionParams {
  definitions: Array<RecommendationDefinition>
  sources: Record<RecommendationSourceId, RecommendationSource>
  experienceId: string
  placeId: string
  experiences: Record<string, App.ExperienceOffer | undefined>
  errors: Record<string, unknown>
  categories: Array<App.ExperienceItemCategory>
  settled?: Partial<Record<RecommendationId, RecommendationSection>>
}

const CITY_LEVELS: Array<App.PlaceType> = ['city']
const EXCLUDED_DISCOVERY_CATEGORY_IDS = new Set([AnythingCategory.id, EXP_CATEGORY_DAY_TRIPS_ID, EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID])

function useRecommendationSource(experienceId: string, placeId: string | undefined, categoryId?: number, enabled = true) {
  const requestKey = `${experienceId}:${placeId}:${categoryId}`
  const [request, setRequest] = useState({ key: requestKey, limit: RECOMMENDATION_CANDIDATE_BATCH })
  const limit = request.key === requestKey ? request.limit : RECOMMENDATION_CANDIDATE_BATCH
  const categoryCodes = useMemo(() => categoryId === undefined ? undefined : [categoryId], [categoryId])
  const list = useExperienceSearchList({ placeId, categoryCodes, enabled, limit, sortBy: 'bestValue', randomize: false, streamingEnabled: true })
  const expand = useCallback(() => {
    setRequest(previous => ({
      key: requestKey,
      limit: (previous.key === requestKey ? previous.limit : RECOMMENDATION_CANDIDATE_BATCH) + RECOMMENDATION_CANDIDATE_BATCH,
    }))
  }, [requestKey])

  return useMemo(() => ({ list, limit, enabled, expand }), [list, limit, enabled, expand])
}

function useExperienceOfferPageRecommendations(experience: App.ExperienceOffer) {
  const dispatch = useAppDispatch()
  const [resolvedPlace] = usePlaceByCoords(experience.location.latitude, experience.location.longitude, CITY_LEVELS)
  const place = resolvedPlace?.type && resolvedPlace.type !== 'city' ? undefined : resolvedPlace
  const categories = useAppSelector(state => state.experience.experienceCategories)
  const categoriesError = useAppSelector(state => state.experience.experienceCategoriesError)
  const experiences = useAppSelector(state => state.experience.experiences)
  const errors = useAppSelector(state => state.experience.experienceErrors)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const locale = useAppSelector(state => state.geo.locale)
  const selectionKey = `${experience.id}:${place?.id}:${currency}:${locale}`
  const [settled, setSettled] = useState<{ key: string, sections: Partial<Record<RecommendationId, RecommendationSection>> }>({ key: selectionKey, sections: {} })
  const primaryCategory = experience.primaryCategory
  const category = primaryCategory?.level === 'parent' ?
    experience.categories.find(item => item.level === 'child' && item.parentId === primaryCategory.id) ?? primaryCategory :
    primaryCategory
  const categoryId = category?.id
  const parentId = category?.parentId ?? (category?.level === 'parent' ? category.id : categories.find(item => item.id === categoryId)?.parentId)
  const enabled = !!place && categories.length > 0 && !categoriesError

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  const specific = useRecommendationSource(experience.id, place?.id, categoryId, enabled && categoryId !== undefined)
  const parent = useRecommendationSource(experience.id, place?.id, parentId, enabled && parentId !== undefined)
  const all = useRecommendationSource(experience.id, place?.id, undefined, enabled)
  const dining = useRecommendationSource(experience.id, place?.id, EXP_CATEGORY_FOOD_AND_DRINK_ID, enabled)
  const wellness = useRecommendationSource(experience.id, place?.id, EXP_CATEGORY_WELLNESS_AND_SPA_ID, enabled)
  const activities = useRecommendationSource(experience.id, place?.id, EXP_CATEGORY_TOP_ACTIVITIES_ID, enabled)

  const sources = useMemo(() => ({ specific, parent, all, dining, wellness, activities }), [specific, parent, all, dining, wellness, activities])
  const definitions = useMemo((): Array<RecommendationDefinition> => [
    { id: 'yaml', source: 'specific', fallback: parentId !== undefined && parentId !== categoryId ? 'parent' : undefined, enabled: categoryId !== undefined },
    { id: 'similar', source: 'parent', enabled: parentId !== undefined, categoryIds: parentId === undefined ? undefined : [parentId] },
    { id: 'new', source: 'all', enabled: true, categoryIds: categories.filter(item => item.level === 'parent' && !EXCLUDED_DISCOVERY_CATEGORY_IDS.has(item.id)).map(item => item.id) },
    { id: 'dining', source: 'dining', enabled: true, categoryIds: [EXP_CATEGORY_FOOD_AND_DRINK_ID] },
    { id: 'wellness', source: 'wellness', enabled: true, categoryIds: [EXP_CATEGORY_WELLNESS_AND_SPA_ID] },
    { id: 'activities', source: 'activities', enabled: true, categoryIds: [EXP_CATEGORY_TOP_ACTIVITIES_ID] },
  ], [parentId, categoryId, categories])

  const selection = useMemo(() => selectExperienceOfferPageRecommendations({
    definitions,
    sources,
    experienceId: experience.id,
    placeId: place?.id ?? '',
    experiences,
    errors,
    categories,
    settled: settled.key === selectionKey ? settled.sections : undefined,
  }), [definitions, sources, experience.id, place?.id, experiences, errors, categories, settled, selectionKey])

  useEffect(() => {
    if (!enabled) return
    const completed = selection.sections.filter(section => section.status !== 'loading')
    if (completed.some(section => settled.key !== selectionKey || !settled.sections[section.id])) {
      setSettled({ key: selectionKey, sections: Object.fromEntries(completed.map(section => [section.id, section])) })
    }
    if (selection.detailIds.length > 0) dispatch(fetchExperiencesById(...selection.detailIds))
    selection.expandSources.forEach(source => sources[source].expand())
  }, [dispatch, enabled, selection, sources, settled, selectionKey])

  return { place, sections: categoriesError ? [] : selection.sections }
}

// Source order already represents MpV. Never sort or shuffle the resulting IDs.
function selectExperienceOfferPageRecommendations(params: SelectionParams) {
  const { definitions, sources, experienceId, placeId, experiences, errors, categories } = params
  const usedIds = new Set([experienceId])
  const detailIds = new Set<string>()
  const expandSources = new Set<RecommendationSourceId>()
  const travelCategoryIds = new Set([
    EXP_CATEGORY_DAY_TRIPS_ID,
    ...categories.filter(category => category.parentId === EXP_CATEGORY_DAY_TRIPS_ID).map(category => category.id),
  ])
  let blocked = false

  function selectSource(sourceId: RecommendationSourceId, excludeTravelEssentials: boolean) {
    const { list, limit, enabled } = sources[sourceId]
    const selected: Array<App.ExperienceOffer> = []
    const pending: Array<string> = []
    if (!enabled || list.placeId !== placeId) {
      return { status: 'loading', experiences: selected, listKey: list.listKey, failed: false } as const
    }
    if (list.error) return { status: 'hidden', experiences: selected, listKey: list.listKey, failed: true } as const
    if (list.fetching) return { status: 'loading', experiences: selected, listKey: list.listKey, failed: false } as const

    for (const id of new Set(list.ids)) {
      if (usedIds.has(id) || errors[id]) continue
      const offer = experiences[id]
      if (!offer) {
        pending.push(id)
      } else if (offer.status === 'ONLINE' && (!excludeTravelEssentials || !offer.categories.some(category =>
        travelCategoryIds.has(category.id) || category.parentId === EXP_CATEGORY_DAY_TRIPS_ID,
      ))) {
        selected.push(offer)
      }
      if (selected.length + pending.length === RECOMMENDATION_MAX_OFFERS) break
    }

    if (pending.length > 0) {
      pending.forEach(id => detailIds.add(id))
      return { status: 'loading', experiences: selected, listKey: list.listKey, failed: false } as const
    }
    if (selected.length < RECOMMENDATION_MAX_OFFERS && list.ids.length >= limit && (list.total == null || list.total > list.ids.length)) {
      expandSources.add(sourceId)
      return { status: 'loading', experiences: selected, listKey: list.listKey, failed: false } as const
    }
    return {
      status: selected.length >= RECOMMENDATION_MIN_OFFERS ? 'ready' : 'hidden',
      experiences: selected,
      listKey: list.listKey,
      failed: false,
    } as const
  }

  const sections = definitions.map((definition): RecommendationSection => {
    const base = { id: definition.id, categoryIds: definition.categoryIds, experiences: [], listKey: '' }
    if (!definition.enabled) return { ...base, status: 'hidden' }
    if (blocked) return { ...base, status: 'loading' }
    const settled = params.settled?.[definition.id]
    if (settled) {
      if (settled.status === 'ready') settled.experiences.forEach(offer => usedIds.add(offer.id))
      return settled
    }

    let selection = selectSource(definition.source, definition.id === 'new')
    if (selection.status === 'hidden' && !selection.failed && definition.fallback) {
      selection = selectSource(definition.fallback, false)
    }
    if (selection.status === 'loading') blocked = true
    if (selection.status === 'ready') selection.experiences.forEach(offer => usedIds.add(offer.id))
    return { ...base, status: selection.status, experiences: selection.experiences, listKey: selection.listKey }
  })

  return { sections, detailIds: [...detailIds], expandSources: [...expandSources] }
}

export default useExperienceOfferPageRecommendations
